import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const KEY = "fj_intro_seen";
const DURATION = 5200;

const Kaaba = () => (
  <svg viewBox="0 0 200 200" className="w-40 h-40 md:w-56 md:h-56 drop-shadow-[0_0_40px_rgba(200,170,0,0.35)]">
    <g>
      <polygon points="100,40 160,70 100,100 40,70" fill="#1a1a1a" />
      <polygon points="40,70 100,100 100,170 40,140" fill="#0b0b0b" />
      <polygon points="160,70 100,100 100,170 160,140" fill="#161616" />
      <polygon points="40,86 100,116 100,128 40,98" fill="#C8AA00" />
      <polygon points="160,86 100,116 100,128 160,98" fill="#E2C44A" />
      <rect x="112" y="128" width="10" height="26" fill="#C8AA00" opacity="0.9" />
    </g>
  </svg>
);

const IntroSplash = () => {
  const [show, setShow] = useState(() => !sessionStorage.getItem(KEY));
  const [phase, setPhase] = useState(0);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const calc = () => setScale(Math.max(window.innerWidth / 1000, window.innerHeight / 600));
    calc();
    window.addEventListener("resize", calc);
    return () => window.removeEventListener("resize", calc);
  }, []);

  useEffect(() => {
    if (!show) return;
    document.body.style.overflow = "hidden";
    const t1 = setTimeout(() => setPhase(1), 1400);
    const t2 = setTimeout(() => setPhase(2), 3000);
    const t3 = setTimeout(() => finish(), DURATION);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); document.body.style.overflow = ""; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const finish = () => { sessionStorage.setItem(KEY, "1"); document.body.style.overflow = ""; setShow(false); };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          key="intro"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, y: "-100%", transition: { duration: 0.9, ease: [0.76, 0, 0.24, 1] } }}
          className="fixed inset-0 z-[200] mesh-band overflow-hidden flex items-center justify-center"
          data-testid="intro-splash"
        >
          <div className="absolute inset-0 star-pattern opacity-[0.12]" />
          <div className="blob w-[600px] h-[600px] bg-jannah-gold/30 -top-40 -left-20" />
          <div className="blob w-[500px] h-[500px] bg-jannah-green-light/40 -bottom-40 -right-10" style={{ animationDelay: "-5s" }} />

          {/* Mataf + Kaaba */}
          <motion.div
            initial={{ opacity: 0, scale: 0.6 }}
            animate={phase < 2 ? { opacity: 1, scale: 1 } : { opacity: 0.18, scale: 1.6, y: 60 }}
            transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
            className="absolute flex items-center justify-center"
          >
            {[460, 360, 270].map((d, i) => (
              <div
                key={d}
                className="absolute rounded-full border border-jannah-gold/30"
                style={{ width: d, height: d, animation: `orbit ${18 + i * 8}s linear infinite ${i % 2 ? "reverse" : ""}` }}
              >
                {Array.from({ length: 14 + i * 6 }).map((_, k) => (
                  <span
                    key={k}
                    className="absolute w-1.5 h-1.5 rounded-full bg-white/80"
                    style={{ top: "50%", left: "50%", transform: `rotate(${(360 / (14 + i * 6)) * k}deg) translate(${d / 2}px) translate(-50%,-50%)` }}
                  />
                ))}
              </div>
            ))}
            <div className="absolute w-[200px] h-[200px] rounded-full bg-jannah-gold/10 blur-2xl" />
            <Kaaba />
          </motion.div>

          {/* Flight path + plane */}
          <svg className="absolute left-1/2 top-1/2 pointer-events-none" viewBox="0 0 1000 600" style={{ width: 1000, height: 600, transform: `translate(-50%,-50%) scale(${scale})` }}>
            <motion.path
              d="M -50 520 C 250 420, 450 120, 1050 80"
              fill="none"
              stroke="url(#trail)"
              strokeWidth="2"
              strokeDasharray="8 10"
              initial={{ pathLength: 0, opacity: 0 }}
              animate={{ pathLength: 1, opacity: phase < 2 ? 0.8 : 0 }}
              transition={{ pathLength: { duration: 2.2, ease: "easeInOut", delay: 0.4 }, opacity: { duration: 0.6 } }}
            />
            <defs>
              <linearGradient id="trail" x1="0" x2="1">
                <stop offset="0%" stopColor="#C8AA00" stopOpacity="0" />
                <stop offset="100%" stopColor="#F5E08B" />
              </linearGradient>
            </defs>
          </svg>
          <div className="absolute left-1/2 top-1/2 pointer-events-none" style={{ width: 1000, height: 600, transform: `translate(-50%,-50%) scale(${scale})`, transformOrigin: "center" }}>
          <motion.div
            className="absolute"
            initial={{ offsetDistance: "0%", opacity: 0 }}
            animate={{ offsetDistance: "100%", opacity: phase < 2 ? [0, 1, 1, 1] : 0 }}
            transition={{ offsetDistance: { duration: 2.2, ease: "easeInOut", delay: 0.4 }, opacity: { duration: 2.2, delay: 0.4, times: [0, 0.1, 0.9, 1] } }}
            style={{ offsetPath: "path('M -50 520 C 250 420, 450 120, 1050 80')", offsetRotate: "auto", width: 0, height: 0, left: 0, top: 0 }}
          >
            <svg viewBox="0 0 24 24" className="w-14 h-14 text-white drop-shadow-[0_6px_14px_rgba(0,0,0,0.5)]" fill="currentColor" style={{ transform: "translate(-50%,-50%) rotate(45deg)" }}>
              <path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z" />
            </svg>
          </motion.div>
          </div>

          {/* Brand reveal */}
          <div className="relative text-center px-6">
            <AnimatePresence>
              {phase >= 2 && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center">
                  <motion.img
                    src="/logo.png"
                    alt="Fly Jannah"
                    initial={{ opacity: 0, scale: 0.7, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                    className="h-28 md:h-40 w-auto bg-white rounded-2xl px-6 py-3 shadow-lift"
                  />
                  <motion.h1
                    className="mt-8 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white"
                    initial="hidden"
                    animate="show"
                    variants={{ show: { transition: { staggerChildren: 0.06, delayChildren: 0.3 } } }}
                  >
                    {"Fly Jannah".split("").map((ch, i) => (
                      <motion.span key={i} variants={{ hidden: { opacity: 0, y: 30, rotateX: -90 }, show: { opacity: 1, y: 0, rotateX: 0 } }} transition={{ duration: 0.5 }} className={`inline-block ${ch === " " ? "w-4" : ""} ${i > 3 ? "text-shimmer" : ""}`}>
                        {ch === " " ? "\u00A0" : ch}
                      </motion.span>
                    ))}
                  </motion.h1>
                  <motion.p initial={{ opacity: 0, letterSpacing: "0.1em" }} animate={{ opacity: 1, letterSpacing: "0.45em" }} transition={{ delay: 1, duration: 0.8 }} className="mt-4 text-xs md:text-sm uppercase text-jannah-gold-light">
                    Tours & Travels · Labbayk Allahumma Labbayk
                  </motion.p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <button onClick={finish} className="absolute bottom-8 right-8 text-xs uppercase tracking-widest text-white/60 hover:text-white border border-white/20 rounded-full px-4 py-2 hover:bg-white/10 transition-colors" data-testid="intro-skip">
            Skip →
          </button>
          <motion.div className="absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-jannah-gold to-jannah-gold-light" initial={{ width: "0%" }} animate={{ width: "100%" }} transition={{ duration: DURATION / 1000, ease: "linear" }} />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default IntroSplash;
